import { createPortal } from 'preact/compat';
import cn from 'classnames';
import { useState, useRef, useCallback } from 'preact/hooks';
import style from './Popover.module.scss';
import type { PopoverProps } from './types.ts';
import { useFocusTrap } from '@/widgets/hooks/useFocusTrap.tsx';
import useOpenDialogAsModal from '@/widgets/hooks/useOpenDialogAsModal.tsx';
import { usePopoverPosition } from './hooks/usePopoverPosition.ts';
import { useAutoPositionedRef } from './hooks/useAutoPositionedRef.ts';
import { useOutsideClickHandler } from './hooks/useOutsideClickHandler.ts';
import { getRootElement } from '@/widgets/utils/layout.ts';

export default function Popover({
  renderTargetElement,
  renderContent,
  isAnchored = true
}: PopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const targetRef = useRef<HTMLElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { popoverStyle, moveTo, movedPosition } = usePopoverPosition(targetRef);
  const close = () => setIsOpen(false);

  const contentRef = useAutoPositionedRef<HTMLDivElement>(
    targetRef,
    moveTo,
    popoverStyle,
    movedPosition,
    isAnchored
  );

  useOutsideClickHandler(targetRef, isOpen, close);
  useFocusTrap(targetRef, contentRef, isOpen);
  useOpenDialogAsModal(dialogRef, isOpen);

  const togglePopover = useCallback(
    () =>
      setIsOpen((stillOpen) => {
        if (!stillOpen && isAnchored) moveTo.topCenter();
        return !stillOpen;
      }),
    [isAnchored]
  );

  function closeOnEscape(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  }

  return (
    <>
      {renderTargetElement({
        ref: targetRef,
        togglePopover,
        isOpen
      })}
      {isOpen &&
        createPortal(
          isAnchored ? (
            <div
              ref={contentRef}
              role="presentation"
              onClick={(e) => e.stopPropagation()}
              class={cn(style.popoverContent, style[movedPosition])}
              onKeyDown={closeOnEscape}
              data-popovercontent
              style={popoverStyle}
            >
              {renderContent({ togglePopover })}
            </div>
          ) : (
            <dialog
              ref={dialogRef}
              role="presentation"
              class={style.dialog}
              data-popovercontent
              onClose={close}
            >
              {renderContent({ togglePopover })}
            </dialog>
          ),
          getRootElement()!
        )}
    </>
  );
}
